import { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { SlidersHorizontal, X } from "lucide-react";
import { api } from "@/lib/api";
import ContentCard from "@/components/ContentCard";
import { COUNTRY_LIST, LANGUAGES, CONTENT_TYPES, typeLabel } from "@/lib/constants";

export default function Browse() {
  const [params, setParams] = useSearchParams();
  const [items, setItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showFilters, setShowFilters] = useState(false);

  const filters = {
    country: params.get("country") || "",
    language: params.get("language") || "",
    category: params.get("category") || "",
    content_type: params.get("content_type") || "",
    subscription: params.get("subscription") || "",
  };

  const setFilter = (key, value) => {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value); else next.delete(key);
    setParams(next);
  };

  const clearAll = () => setParams(new URLSearchParams());

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/content", { params: filters });
      setItems(data);
    } finally { setLoading(false); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get("/categories").then((r) => setCategories(r.data)).catch(() => {}); }, []);

  const activeCount = Object.values(filters).filter(Boolean).length;

  return (
    <div className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-10 py-8">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-display font-black text-3xl sm:text-4xl">Browse</h1>
          <p className="text-white/50 text-sm mt-1">{items.length} titles across Africa</p>
        </div>
        <button onClick={() => setShowFilters((s) => !s)} data-testid="toggle-filters"
          className="flex items-center gap-2 px-4 py-2 rounded-full bg-white/5 border border-white/10 hover:bg-white/10 transition-colors text-sm">
          <SlidersHorizontal className="w-4 h-4" /> Filters
          {activeCount > 0 && <span className="w-5 h-5 rounded-full bg-[#E50914] text-xs flex items-center justify-center">{activeCount}</span>}
        </button>
      </div>

      <div className={`${showFilters ? "grid" : "hidden md:grid"} grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-3 mb-6 p-4 glass rounded-2xl`}>
        <FilterSelect label="Country" testid="filter-country" value={filters.country} onChange={(v) => setFilter("country", v)}
          options={COUNTRY_LIST.map((c) => ({ value: c.code, label: `${c.flag} ${c.name}` }))} />
        <FilterSelect label="Language" testid="filter-language" value={filters.language} onChange={(v) => setFilter("language", v)}
          options={LANGUAGES.map((l) => ({ value: l, label: l }))} />
        <FilterSelect label="Category" testid="filter-category" value={filters.category} onChange={(v) => setFilter("category", v)}
          options={categories.map((c) => ({ value: c.name, label: c.name }))} />
        <FilterSelect label="Type" testid="filter-type" value={filters.content_type} onChange={(v) => setFilter("content_type", v)}
          options={CONTENT_TYPES.map((t) => ({ value: t, label: typeLabel(t) }))} />
        <FilterSelect label="Access" testid="filter-subscription" value={filters.subscription} onChange={(v) => setFilter("subscription", v)}
          options={[{ value: "free", label: "Free" }, { value: "premium", label: "Premium" }]} />
        {activeCount > 0 && (
          <button onClick={clearAll} data-testid="clear-filters" className="lg:col-span-5 flex items-center justify-center gap-1 text-sm text-[#E50914] hover:underline">
            <X className="w-4 h-4" /> Clear all filters
          </button>
        )}
      </div>

      {loading ? (
        <div className="min-h-[40vh] flex items-center justify-center">
          <div className="w-10 h-10 border-4 border-[#E50914] border-t-transparent rounded-full animate-spin" />
        </div>
      ) : items.length === 0 ? (
        <div className="min-h-[30vh] flex flex-col items-center justify-center text-white/40">
          <p className="text-lg font-display">No titles match these filters</p>
          <button onClick={clearAll} className="mt-3 text-[#E50914] hover:underline text-sm">Reset filters</button>
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4" data-testid="browse-grid">
          {items.map((it, i) => <ContentCard key={it.id} item={it} index={i} />)}
        </div>
      )}
    </div>
  );
}

const FilterSelect = ({ label, value, onChange, options, testid }) => (
  <div>
    <label className="text-[10px] uppercase tracking-wider text-white/40 mb-1 block">{label}</label>
    <select value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid}
      className="w-full h-10 px-3 rounded-lg bg-white/5 border border-white/10 focus:border-[#E50914]/60 focus:outline-none text-sm">
      <option value="" className="bg-[#1A1724]">All</option>
      {options.map((o) => <option key={o.value} value={o.value} className="bg-[#1A1724]">{o.label}</option>)}
    </select>
  </div>
);
